import React from 'react';
import { useTheme } from '@emotion/react';
import {
  FormControlLabel,
  Radio,
  RadioGroup,
  type FormControlLabelProps,
  type RadioGroupProps,
  type RadioProps,
} from '@mui/material';
import _ from 'lodash';
import { type ExperienceMultiSurveyCardReasonSection } from '@bbkAdminRedux/rtkq/experience.slice';
import { type ReactFCC } from '@bbkAdminUtils/utility-types';
import { type NextStepHandler } from '@bbkFunnel/events';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import {
  ReasonOtherText,
  type ReasonOtherHandler,
} from '@bbkFunnelComponents/survey/reasons/reasons-other-text';
import {
  type SurveyMultiRowResponse,
  type SurveyMultiRowResponseHandler,
} from '@bbkFunnelComponents/survey-multi-row';
import isReasonOther from '@bbkFunnelUtils/reason-other';

export type ReasonOtherTextClearHandler = (cb: () => void) => void;

type Props = {
  items: ExperienceMultiSurveyCardReasonSection['options'];
  response: SurveyMultiRowResponse;
  handler: SurveyMultiRowResponseHandler;
  tierValue: string;
  nextStep: NextStepHandler;
  continue: () => void;
  question: ExperienceMultiSurveyCardReasonSection;
  handleReasonOtherText?: ReasonOtherHandler;
  clearReasonOtherText?: ReasonOtherTextClearHandler;
  error?: string;
};
export const ReasonsContent: ReactFCC<Props> = ({
  response,
  question,
  handleReasonOtherText,
  items,
  error,
  clearReasonOtherText,
  nextStep,
  continue: continueFn,
  handler,
  tierValue,
}) => {
  const theme = useTheme() as EmotionTheme;

  const ErrorDisplay = () => (
    <div
      css={{
        color: theme.errorVal,
        fontSize: 12,
        paddingTop: 5,
      }}
    >
      {error}
    </div>
  );

  const ChangeFn: RadioGroupProps['onChange'] = (e, value) => {
    const item = items.find((item) => item.value === value);
    const action = _.get(item, 'action');

    // Save answer
    handler(e, value, item?.tier ?? 'tier0', tierValue);
    const doWork = () => {
      nextStep(action);
      continueFn();
    };
    if (action !== 'open_text_input') {
      if (clearReasonOtherText && action) {
        clearReasonOtherText(() => {
          doWork();
        });
      } else if (action) {
        doWork();
      }
    }
  };

  const selectedValue = response.reason_for_leaving.find(
    (x) => x.name === question.name
  );

  const filteredReasons = response.reason_for_leaving.filter((r) => {
    return r.name === 'reason_for_leaving' && isReasonOther(r.value);
  });
  const showReasonOtherText = filteredReasons.length === 1;

  const reasonOtherTextVal =
    filteredReasons.length === 1 && response.reason_other !== undefined
      ? response.reason_other
      : '';

  const radioColor = error ? theme.errorVal : theme.textColor;
  const radioColorChecked = error ? theme.errorVal : theme.primaryColor;

  const WrappedLabel = (props: FormControlLabelProps) => (
    <FormControlLabel
      {...props}
      sx={{
        margin: 0,
        '& .MuiFormControlLabel-label': {
          color: radioColor,
          fontFamily: theme.fontFamily,
        },
      }}
    />
  );

  const WrappedRadio = (props: RadioProps) => (
    <Radio
      {...props}
      sx={{
        color: radioColor,
        margin: '0 0 0 -10px',
        opacity: 0.4,
        '&.Mui-checked': {
          color: radioColorChecked,
          opacity: 1,
        },
      }}
    />
  );

  return (
    <div>
      <RadioGroup
        name={question.name}
        value={selectedValue?.value ?? ''}
        onChange={ChangeFn}
      >
        {items.map((item) => (
          <WrappedLabel
            control={<WrappedRadio />}
            key={item.value}
            value={item.value}
            label={item.label}
          />
        ))}
      </RadioGroup>
      {handleReasonOtherText && (
        <div>
          <ReasonOtherText
            showOther={showReasonOtherText}
            reasonOtherTextVal={reasonOtherTextVal}
            handleOtherText={handleReasonOtherText}
            tierValue={tierValue}
            question={question}
            selectedItem={selectedValue}
          />
        </div>
      )}
      {error && <ErrorDisplay />}
    </div>
  );
};
